import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";

interface MenuItem {
  id: string;
  name: string;
  price: number;
  image?: string;
  isPopular: boolean;
  itemType: string;
}

interface MenuGridProps {
  items: MenuItem[];
  onItemClick: (item: MenuItem) => void;
  loading?: boolean;
}

function MenuItemTile({ item, onClick }: { item: MenuItem; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-start rounded-2xl border border-surface-border bg-surface p-4",
        "text-left transition-all duration-200 min-h-[120px]",
        "hover:bg-white/8 hover:border-surface-border-2 hover:shadow-lg hover:shadow-white/[0.02]",
        "active:scale-[0.97] active:bg-white/5",
      )}
    >
      {item.isPopular && (
        <div className="absolute top-3 right-3">
          <Badge variant="warning" size="sm">Popular</Badge>
        </div>
      )}
      {item.image && (
        <div className="mb-3 h-20 w-full overflow-hidden rounded-xl bg-surface-2">
          <img
            src={item.image}
            alt={item.name}
            className="h-full w-full object-cover"
          />
        </div>
      )}
      <span className="text-base font-medium text-white leading-tight">{item.name}</span>
      <span className="mt-auto pt-2 text-sm font-semibold text-accent">{formatCurrency(item.price)}</span>
    </button>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-surface-border bg-surface p-4 min-h-[120px]">
          <Skeleton className="mb-3 h-20 w-full rounded-xl" />
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      ))}
    </div>
  );
}

export function MenuGrid({ items, onItemClick, loading }: MenuGridProps) {
  if (loading) {
    return <SkeletonGrid />;
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <svg className="h-12 w-12 text-text-dim mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
        <p className="text-text-muted text-base">No menu items found</p>
        <p className="text-text-dim text-sm mt-1">Try selecting a different category</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map((item) => (
        <MenuItemTile key={item.id} item={item} onClick={() => onItemClick(item)} />
      ))}
    </div>
  );
}
